import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FC, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode, forwardRef } from 'react';
import { Theme } from '../../types/enums';
import Button, { ButtonStyles, ButtonTypes } from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import { FAIcon } from '../Icons/Icons';
import { TextLinkPropsTypes, TextLinkStyle, TextLinkUnderlineStyle } from '../TextLink/TextLink';
import RawTextLink from '../TextLink/RawTextLink';
import styles from './Modal.module.scss';
import RawModal from './RawModal';
import { ButtonAlignment, TextAlign } from './types/enums';
import { ModalPropsTypes } from './types/propTypes';

const handleActionButtonClick = (
  e: ReactKeyboardEvent | ReactMouseEvent,
  func?: (e: ReactKeyboardEvent | ReactMouseEvent) => void,
) => {
  func?.(e);
};

const getPrimaryButton = (
  textLinkProps?: TextLinkPropsTypes,
  secondaryButtonLabel?: string,
  primaryButton?: ReactNode,
  primaryButtonLabel?: string,
  onPrimaryButtonClick?: (e: ReactKeyboardEvent | ReactMouseEvent) => void,
) => {
  let primaryButtonCls = styles['primary-button'];
  if (textLinkProps?.text && !secondaryButtonLabel) {
    primaryButtonCls = styles['primary-button-with-textlink'];
  }
  if (primaryButton) {
    return primaryButton;
  } else if (primaryButtonLabel) {
    return (
      <span className={primaryButtonCls}>
        <Button
          title={primaryButtonLabel}
          type={ButtonTypes.BUTTON}
          onClick={(e) => handleActionButtonClick(e, onPrimaryButtonClick)}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="primary-button-test-id"
        />
      </span>
    );
  } else {
    return '';
  }
};

const getSecondaryButton = (
  secondaryButton?: ReactNode,
  secondaryButtonLabel?: string,
  onSecondaryButtonClick?: (e: ReactKeyboardEvent | ReactMouseEvent) => void,
) => {
  if (secondaryButton) {
    return secondaryButton;
  }
  return secondaryButtonLabel ? (
    <span className={styles['secondary-button']}>
      <Button
        title={secondaryButtonLabel}
        type={ButtonTypes.BUTTON}
        onClick={(e) => handleActionButtonClick(e, onSecondaryButtonClick)}
        buttonStyle={ButtonStyles.OUTLINE}
        theme={Theme.BLACK}
        data-testid="secondary-button-test-id"
      />
    </span>
  ) : (
    ''
  );
};
const Modal: FC<ModalPropsTypes> = forwardRef<HTMLDivElement, ModalPropsTypes>(
  (
    {
      headline,
      children,
      primaryButtonLabel,
      secondaryButtonLabel,
      textLinkProps,
      checkBox,
      textAlign = TextAlign.CENTER,
      containerWidth,
      closeIcon = true,
      closeOnBackdropClick = true,
      onSecondaryButtonClick,
      onPrimaryButtonClick,
      onClose,
      primaryButton,
      secondaryButton,
      buttonAlignment = ButtonAlignment.DEFAULT,
      id,
      useReactPortal,
      portalContainerId,
      ...restProps
    },
    ref,
  ) => {
    const header = (
      <div className={styles['modal-header-section']}>
        <div className={styles['modal-icon-section']}>
          {closeIcon && (
            <FAIcon
              className={styles['close-icon']}
              data-testid="close-icon-test-id"
              iconName={faClose.iconName}
              theme={Theme.BLACK}
              onClick={(e) => handleActionButtonClick(e, onClose)}
            />
          )}
        </div>
        <section
          className={`${styles['modal-headline']} ${styles[`modal-headline-${textAlign}`]}`}
          data-testid="modal-healine-test-id"
        >
          {headline}
        </section>
      </div>
    );

    const textLink = textLinkProps?.text ? (
      <RawTextLink
        textLinkStyle={TextLinkStyle.UNDERLINE}
        underline={TextLinkUnderlineStyle.FIXED}
        {...textLinkProps}
        className={`${styles['text-link']}`}
        data-testid="test-link-test-id"
      />
    ) : (
      <></>
    );

    const footer = (
      <div className={styles['modal-footer-section']}>
        {checkBox?.text && <Checkbox {...checkBox} />}
        <div className={`${styles['modal-button-container']} ${styles[`align-${buttonAlignment}-button`]}`}>
          {secondaryButtonLabel || secondaryButton
            ? getSecondaryButton(secondaryButton, secondaryButtonLabel, onSecondaryButtonClick)
            : textLink}
          {getPrimaryButton(
            textLinkProps,
            secondaryButtonLabel,
            primaryButton,
            primaryButtonLabel,
            onPrimaryButtonClick,
          )}
        </div>

        {(secondaryButtonLabel || secondaryButton) && textLink}
      </div>
    );

    return (
      <RawModal
        {...restProps}
        id={id}
        header={header}
        footer={footer}
        textAlign={textAlign}
        closeOnBackdropClick={closeOnBackdropClick}
        onClose={onClose}
        containerWidth={containerWidth}
        ref={ref}
        useReactPortal={useReactPortal}
        portalContainerId={portalContainerId}
      >
        {children}
      </RawModal>
    );
  },
);
Modal.displayName = 'Modal';
export default Modal;
export { ButtonAlignment, TextAlign };
